/**
 * Dev-Stack Authentication Utility
 *
 * Provides authentication helpers for dev-stack (Mockoon) compatibility.
 * Detects dev-stack mode and provides mock JWT tokens when needed.
 *
 * GW-529: Added timeout handling to prevent infinite loading when
 * fetchAuthSession hangs due to Cognito misconfiguration.
 */

import { fetchAuthSession } from 'aws-amplify/auth';

import { getConfig } from '@/config/runtimeConfig';
import { authEvents } from '@/lib/authEvents';
import { isTokenExpired } from '@/lib/jwtUtils.util';
/**
 * Timeout for auth session fetch operations (10 seconds)
 * GW-529: Prevents indefinite hanging when Cognito is misconfigured
 */
export const AUTH_SESSION_TIMEOUT_MS = 10000;

/**
 * Wraps a promise with a timeout. If the promise doesn't resolve
 * within the specified time, it rejects with a timeout error.
 *
 * GW-529: Used to prevent fetchAuthSession from hanging indefinitely.
 *
 * @param promise - The promise to wrap
 * @param timeoutMs - Timeout in milliseconds
 * @param timeoutMessage - Error message when timeout occurs
 * @returns The promise result or throws timeout error
 */
export async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutMessage: string
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(timeoutMessage));
    }, timeoutMs);
  });

  try {
    const result = await Promise.race([promise, timeoutPromise]);
    clearTimeout(timeoutId!);
    return result;
  } catch (error) {
    clearTimeout(timeoutId!);
    throw error;
  }
}

/**
 * Detect if running in dev-stack mode
 * Dev-stack mode indicators:
 * - API URL points to localhost:3001 (Mockoon)
 * - Cognito endpoint is set (cognito-local)
 *
 * Note: The /dev-api Vite proxy path is NOT dev-stack mode — it proxies to
 * real dev backends that require real Cognito tokens.
 */
export function isDevStackMode(): boolean {
  const config = getConfig();
  const isMockoonApi = config.apiBaseUrl.includes('localhost:3001');
  const isCognitoLocal = config.cognito.endpoint?.includes('localhost') ?? false;
  return isMockoonApi || isCognitoLocal;
}

/**
 * Generate a mock JWT token for dev-stack testing
 * This token is NOT cryptographically valid but can be decoded
 */
export function generateMockJWT(): string {
  const header = {
    alg: 'HS256',
    typ: 'JWT',
  };

  const payload = {
    sub: 'dev-stack-user-id',
    email: 'dev@local.test',
    'cognito:username': 'dev-user',
    given_name: 'Dev',
    family_name: 'User',
    'custom:orgId': 'dev-org-id',
    'custom:orgName': 'Dev Organization',
    'custom:roles': '["global_admin", "org_admin"]',
    'custom:permissions': '["read", "write", "admin"]',
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + 3600, // 1 hour
  };

  // Base64 encode (note: this is NOT a secure JWT, just for dev-stack mocking)
  const encodeBase64 = (obj: Record<string, unknown>) => btoa(JSON.stringify(obj));

  const encodedHeader = encodeBase64(header).replace(/=/g, '');
  const encodedPayload = encodeBase64(payload).replace(/=/g, '');
  const signature = 'dev-stack-mock-signature';

  return `${encodedHeader}.${encodedPayload}.${signature}`;
}

/**
 * Get authentication token with dev-stack support
 *
 * Behavior:
 * - Dev-stack mode: Returns mock JWT token (no Amplify call)
 * - Production mode: Calls Amplify fetchAuthSession with timeout
 * - On timeout: Returns null with warning (graceful degradation)
 * - On error: Returns null (graceful degradation)
 * - GW-2099: When initial fetch returns no tokens (expired session),
 *   attempts a forced refresh before returning null
 *
 * GW-529: Added timeout handling to prevent infinite loading when
 * fetchAuthSession hangs due to Cognito misconfiguration.
 *
 * @param options.forceRefresh - Force a token refresh from Cognito
 * @returns JWT token string or null
 */
export async function getAuthToken(options?: { forceRefresh?: boolean }): Promise<string | null> {
  // In dev-stack mode, use mock JWT
  if (isDevStackMode()) {
    return generateMockJWT();
  }

  // In production mode, use real Amplify auth with timeout
  try {
    const session = await withTimeout(
      fetchAuthSession({ forceRefresh: options?.forceRefresh }),
      AUTH_SESSION_TIMEOUT_MS,
      `Auth session timed out after ${AUTH_SESSION_TIMEOUT_MS}ms`
    );
    const token = session.tokens?.idToken?.toString();

    // If this is already a force refresh call, just return what we got
    if (options?.forceRefresh) {
      return token || null;
    }

    // GW-4768: If token exists but is expired, attempt force refresh before proceeding
    if (token && isTokenExpired(token)) {
      const refreshedToken = await getAuthToken({ forceRefresh: true });
      if (!refreshedToken || isTokenExpired(refreshedToken)) {
        // Refreshed token is still expired or null — session is truly expired
        authEvents.emit('session-expired');
        return null;
      }
      return refreshedToken;
    }

    // GW-2099: If no token returned and we haven't tried a force refresh yet,
    // attempt to refresh the session before giving up
    if (!token) {
      const refreshedToken = await getAuthToken({ forceRefresh: true });
      if (!refreshedToken) {
        // Refresh also failed — session is truly expired
        authEvents.emit('session-expired');
      }
      return refreshedToken;
    }

    return token;
  } catch (error) {
    // GW-529: Distinguish between timeout and other errors for better debugging
    if (error instanceof Error && error.message.includes('timed out')) {
      console.warn(
        '[DevStackAuth] Auth session timed out. This may indicate Cognito misconfiguration or network issues.'
      );
    } else {
      console.error('[DevStackAuth] Failed to get auth token:', error);
    }
    return null;
  }
}
